import React from "react";

function formatSeason(row) {
    const raw = String(row.season || row.year || "").replace(/\s*Playoffs$/i, "");
    let label = raw;
    if (/^\d{8}$/.test(raw)) {
        label = `${raw.slice(2, 4)}–${raw.slice(6)}`;
    } else if (/^\d{4}$/.test(raw)) {
        label = raw;
    }
    return { label, playoff: Boolean(row.is_playoff) };
}

function formatCell(value) {
    if (value == null || value === "") return "—";
    return value;
}

function formatPlusMinus(value) {
    if (value == null || value === "") return "—";
    const num = Number(value);
    if (!Number.isFinite(num)) return value;
    if (num > 0) return `+${num}`;
    return String(num);
}

function pointsForRow(row) {
    if (row.points != null && row.points !== "") return Number(row.points);
    if (row.goals == null && row.assists == null) return null;
    return (Number(row.goals) || 0) + (Number(row.assists) || 0);
}

function formatPpg(row) {
    const gp = Number(row.games_played);
    const pts = pointsForRow(row);
    if (!gp || pts == null || Number.isNaN(Number(pts))) return "—";
    return (Number(pts) / gp).toFixed(2);
}

function formatGaa(value) {
    if (value == null || value === "") return "—";
    const num = parseFloat(value);
    return Number.isFinite(num) ? num.toFixed(2) : value;
}

function formatSvPct(value) {
    if (value == null || value === "") return "—";
    const num = parseFloat(value);
    if (!Number.isFinite(num)) return value;
    return num >= 1 ? num.toFixed(3) : `.${String(Math.round(num * 1000)).padStart(3, "0")}`;
}

function formatFactor(value) {
    if (value == null || Number.isNaN(Number(value))) return "—";
    return Number(value).toFixed(2);
}

function leagueLabel(row) {
    return (
        row.nhl_api_league_abbrev ||
        row.league_name ||
        row.team?.league?.league_name ||
        "—"
    );
}

function teamLabel(row) {
    return row.team_name || row.nhl_api_team_name || row.team?.team_name || "—";
}

function rowKey(row, index) {
    return [
        row.player_id,
        row.nhl_season,
        row.year,
        row.season,
        row.nhl_api_league_abbrev,
        row.nhl_api_team_name,
        row.team_id,
        row.game_type_id,
        row.sequence,
        row.is_playoff,
        index,
    ].join("-");
}

function PlayerStatsTable({
    rows,
    isGoalie = false,
    showNhle = false,
    showPlusMinus = false,
    recordHeader = "W-L-OT",
}) {
    return (
        <div className="nhl-stats-table-wrap">
            <table className="nhl-stats-table">
                <thead>
                    <tr>
                        <th className="nhl-stats-col-league">Lg</th>
                        <th className="nhl-stats-col-team">Team</th>
                        <th className="nhl-stats-col-season">Season</th>
                        {showNhle && <th className="nhl-stats-col-num">Fac</th>}
                        <th className="nhl-stats-col-num">GP</th>
                        {isGoalie ? (
                            <>
                                <th className="nhl-stats-col-record">{recordHeader}</th>
                                <th className="nhl-stats-col-num">GAA</th>
                                <th className="nhl-stats-col-num">SV%</th>
                                <th className="nhl-stats-col-num">SO</th>
                                <th className="nhl-stats-col-num">PIM</th>
                            </>
                        ) : (
                            <>
                                <th className="nhl-stats-col-num">{showNhle ? "G*" : "G"}</th>
                                <th className="nhl-stats-col-num">{showNhle ? "A*" : "A"}</th>
                                <th className="nhl-stats-col-num">{showNhle ? "PTS*" : "PTS"}</th>
                                <th className="nhl-stats-col-num">{showNhle ? "PPG*" : "PPG"}</th>
                                {showPlusMinus && <th className="nhl-stats-col-num">+/-</th>}
                                <th className="nhl-stats-col-num">PIM</th>
                            </>
                        )}
                    </tr>
                </thead>
                <tbody>
                    {rows.map((row, index) => {
                        const season = formatSeason(row);
                        return (
                            <tr
                                key={rowKey(row, index)}
                                className={season.playoff ? "nhl-stats-row--playoff" : undefined}
                            >
                                <td className="nhl-stats-col-league">
                                    <span title={row.league_name || leagueLabel(row)}>
                                        {leagueLabel(row)}
                                    </span>
                                </td>
                                <td className="nhl-stats-col-team">
                                    <span title={teamLabel(row)}>{teamLabel(row)}</span>
                                </td>
                                <td className="nhl-stats-col-season">
                                    <span className="nhl-stats-season">
                                        {season.label}
                                        {season.playoff ? (
                                            <span className="nhl-stats-playoff">PO</span>
                                        ) : null}
                                    </span>
                                </td>
                                {showNhle && (
                                    <td className="nhl-stats-col-num">
                                        {formatFactor(row.nhle_factor)}
                                    </td>
                                )}
                                <td className="nhl-stats-col-num">{formatCell(row.games_played)}</td>
                                {isGoalie ? (
                                    <>
                                        <td className="nhl-stats-col-record">
                                            {formatCell(row.record)}
                                        </td>
                                        <td className="nhl-stats-col-num">
                                            {formatGaa(row.goals_against_average)}
                                        </td>
                                        <td className="nhl-stats-col-num">
                                            {formatSvPct(row.save_percentage)}
                                        </td>
                                        <td className="nhl-stats-col-num">
                                            {formatCell(row.shutouts)}
                                        </td>
                                        <td className="nhl-stats-col-num">
                                            {formatCell(row.penalty_minutes)}
                                        </td>
                                    </>
                                ) : (
                                    <>
                                        <td className="nhl-stats-col-num">{formatCell(row.goals)}</td>
                                        <td className="nhl-stats-col-num">{formatCell(row.assists)}</td>
                                        <td className="nhl-stats-col-num">
                                            {formatCell(pointsForRow(row))}
                                        </td>
                                        <td className="nhl-stats-col-num">{formatPpg(row)}</td>
                                        {showPlusMinus && (
                                            <td className="nhl-stats-col-num">
                                                {formatPlusMinus(row.plus_minus)}
                                            </td>
                                        )}
                                        <td className="nhl-stats-col-num">
                                            {formatCell(row.penalty_minutes)}
                                        </td>
                                    </>
                                )}
                            </tr>
                        );
                    })}
                </tbody>
            </table>
        </div>
    );
}

export default PlayerStatsTable;
